import { useState } from "react";
import { Button, Dialog, IconButton } from "@sortera/react-components";
import { PlusIcon, TrashIcon, TwoChevronsLeftIcon } from "@sortera/react-icons";
import type { ConversationDto, ProjectDto } from "../../types/index.ts";
import { ProjectSwitcher } from "../project-switcher/index.ts";
import { groupByDay } from "./utils.ts";
import styles from "./sidebar.module.css";

export interface SidebarProps {
  projects: ProjectDto[] | null;
  repo: string | null;
  conversations: ConversationDto[];
  conversationId: string | null;
  onSelectProject: (repo: string) => void;
  onNewConversation: () => void;
  onSelectConversation: (id: string) => void;
  onDeleteConversation: (id: string) => void;
  /** Folds the panel away, beside the chat. The rail has the button that brings it back. */
  onHide?: () => void;
}

/** The project and its conversations — the second column, beside the rail. */
export function Sidebar(props: SidebarProps) {
  const { projects, repo, conversations, conversationId } = props;
  const [confirm, setConfirm] = useState<ConversationDto | null>(null);

  return (
    <aside className={styles.panel} aria-label="Projekt och konversationer">
      <div className={styles.head}>
        <h2 className={`sc-heading ${styles.title}`}>Chatt</h2>
        {props.onHide && (
          <span className={styles.hide}>
            <IconButton
              aria-label="Dölj sidomenyn"
              title="Dölj sidomenyn"
              color="secondary"
              size="sm"
              icon={<TwoChevronsLeftIcon />}
              onClick={props.onHide}
            />
          </span>
        )}
      </div>

      <div className={styles.picker}>
        <ProjectSwitcher projects={projects} repo={repo} onSelect={props.onSelectProject} />
        <Button color="secondary" size="small" icon={<PlusIcon />} disabled={!repo} onClick={props.onNewConversation}>
          Ny konversation
        </Button>
      </div>

      <div className={styles.list}>
        {repo && conversations.length === 0 && <p className={styles.empty}>Inga konversationer ännu.</p>}
        {groupByDay(conversations).map(([day, items]) => (
          <section key={day} className={styles.group}>
            <h3 className={styles.day}>{day}</h3>
            <ul className={styles.rows}>
              {items.map((c) => (
                <li key={c.id} className={styles.row} data-selected={c.id === conversationId}>
                  <button type="button" className={styles.open} onClick={() => props.onSelectConversation(c.id)} title={c.title}>
                    {c.title}
                  </button>
                  <span className={styles.action}>
                    <IconButton aria-label={`Ta bort ${c.title}`} color="secondary" size="sm" icon={<TrashIcon />} onClick={() => setConfirm(c)} />
                  </span>
                </li>
              ))}
            </ul>
          </section>
        ))}
      </div>

      <Dialog open={Boolean(confirm)} onOpenChange={(open) => !open && setConfirm(null)}>
        <Dialog.Content title="Ta bort konversationen?">
          <Dialog.Description>
            ”{confirm?.title}” försvinner från listan. Ändringar som redan sparats i projektet ligger kvar.
          </Dialog.Description>
          <Dialog.Actions>
            <Dialog.Close render={<Button variant="outlined" color="secondary">Avbryt</Button>} />
            <Dialog.Close
              render={
                <Button color="error" onClick={() => confirm && props.onDeleteConversation(confirm.id)}>
                  Ta bort
                </Button>
              }
            />
          </Dialog.Actions>
        </Dialog.Content>
      </Dialog>
    </aside>
  );
}
